import { useState } from 'react'
import { Eye, Search, AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useUser } from '@/contexts/UserContext'
import { apiClient } from '@/lib/api'
import { useQuery } from '@tanstack/react-query'
import { User } from '@/types'
import { useToast } from '@/hooks/use-toast'
import { GRACKLE_COLORS, CATEGORICAL, SEMANTIC } from '@/lib/theme'

interface ImpersonateUserModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * ImpersonateUserModal - Modal for selecting a user to impersonate
 * WCAG 2.2 AA compliant with proper focus management and ARIA attributes
 */
export function ImpersonateUserModal({ open, onOpenChange }: ImpersonateUserModalProps) {
  const { startImpersonation } = useUser()
  const { toast } = useToast()
  const [search, setSearch] = useState('')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isStarting, setIsStarting] = useState(false)

  // Fetch impersonatable users (non-admin, active users)
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['impersonatable-users', search],
    queryFn: () => apiClient.getImpersonatableUsers({ search, limit: 10000 }),
    enabled: open,
  })

  const users = usersData?.data ?? []

  const handleSelectUser = (user: User) => {
    setSelectedUser(user)
    setIsConfirming(true)
  }

  const handleConfirm = async () => {
    if (!selectedUser) return

    setIsStarting(true)
    try {
      await startImpersonation(selectedUser.id)
      toast({
        title: 'Impersonation started',
        description: `You are now viewing the CRM as ${selectedUser.name}`,
      })
      onOpenChange(false)
      // Reload to apply impersonation context
      window.location.reload()
    } catch (error) {
      console.error('Failed to start impersonation:', error)
      toast({
        title: 'Failed to start impersonation',
        description: 'An error occurred. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsStarting(false)
    }
  }

  const handleBack = () => {
    setIsConfirming(false)
    setSelectedUser(null)
  }

  const handleClose = () => {
    setSearch('')
    setSelectedUser(null)
    setIsConfirming(false)
    onOpenChange(false)
  }

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" aria-describedby="impersonate-dialog-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" aria-hidden="true" />
            {isConfirming ? 'Confirm Impersonation' : 'View as User'}
          </DialogTitle>
          <DialogDescription id="impersonate-dialog-description">
            {isConfirming
              ? 'You are about to view the CRM as another user in read-only mode.'
              : 'Select a user to view the CRM from their perspective. You will be in read-only mode and cannot perform any actions.'}
          </DialogDescription>
        </DialogHeader>

        {!isConfirming ? (
          <>
            {/* Search input */}
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                placeholder="Search users by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search users"
              />
            </div>

            {/* User list */}
            <ScrollArea className="h-[300px] pr-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">Loading users...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">
                    {search ? 'No users found matching your search' : 'No users available'}
                  </p>
                </div>
              ) : (
                <ul
                  className="space-y-2"
                  role="listbox"
                  aria-label="Available users to impersonate"
                >
                  {[...users]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((user) => (
                      <li key={user.id}>
                        <button
                          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring text-left"
                          onClick={() => handleSelectUser(user)}
                          role="option"
                          aria-selected="false"
                        >
                          <Avatar className="h-10 w-10">
                            <AvatarFallback
                              className="text-sm font-medium"
                              style={{
                                backgroundColor: CATEGORICAL.powder.tint,
                                color: GRACKLE_COLORS.navy,
                              }}
                            >
                              {getUserInitials(user.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{user.name}</p>
                            <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                          </div>
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </ScrollArea>
          </>
        ) : (
          /* Confirmation view */
          <div className="space-y-4">
            <div
              className="flex items-center gap-4 p-4 rounded-lg"
              style={{
                backgroundColor: SEMANTIC.warning.fill,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: CATEGORICAL.clay.solid,
              }}
            >
              <AlertTriangle
                className="h-8 w-8 flex-shrink-0"
                style={{ color: CATEGORICAL.clay.solid }}
                aria-hidden="true"
              />
              <div>
                <p className="font-medium" style={{ color: SEMANTIC.warning.text }}>
                  View as {selectedUser?.name}
                </p>
                <p className="text-sm" style={{ color: SEMANTIC.warning.text }}>
                  You will see the CRM from this user's perspective. All actions will be disabled.
                </p>
              </div>
            </div>

            {selectedUser && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <Avatar className="h-12 w-12">
                  <AvatarFallback
                    className="font-medium"
                    style={{
                      backgroundColor: CATEGORICAL.powder.tint,
                      color: GRACKLE_COLORS.navy,
                    }}
                  >
                    {getUserInitials(selectedUser.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{selectedUser.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {isConfirming ? (
            <>
              <Button variant="outline" onClick={handleBack} disabled={isStarting}>
                Back
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={isStarting}
                className="hover:opacity-70"
                style={{
                  backgroundColor: SEMANTIC.warning.fill,
                  color: SEMANTIC.warning.text,
                }}
              >
                {isStarting ? 'Starting...' : 'Start Viewing'}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
